/** The layer-settings keys that hold a layer's flat-content rule. */
export const FLAT_SETTINGS_KEYS = ['flatContentMode', 'flatContentHeight', 'flatContentDrapeLayerId'] as const;
type FlatKey = (typeof FLAT_SETTINGS_KEYS)[number];
export type FlatSettingsLike = Partial<Record<FlatKey, string | number | null | undefined>>;

// How the server reads an absent key: mode AUTO, the others null.
const norm = (key: FlatKey, v: unknown) => v ?? (key === 'flatContentMode' ? 'AUTO' : null);

/**
 * `local` without each flat key whose value equals the server's (`remote`).
 * A per-user object carrying a flat key wins over the server key by key
 * (`flatRuleOf`), so a dialog that wrote its seeded AUTO back on every save
 * pinned the user to it and hid any later "for everyone" change — their own
 * bulk write included. Only a key the user actually set apart is kept.
 */
export function omitFlatKeysMatching<T extends object>(local: T, remote: FlatSettingsLike | null | undefined): T {
  const out = { ...local } as Record<string, unknown>;
  for (const key of FLAT_SETTINGS_KEYS) {
    if (key in out && norm(key, out[key]) === norm(key, remote?.[key])) delete out[key];
  }
  return out as T;
}

/** `local` without any flat key (the server's rule shows through again). */
export function withoutFlatKeys<T extends object>(local: T): T {
  const out = { ...local } as Record<string, unknown>;
  for (const key of FLAT_SETTINGS_KEYS) delete out[key];
  return out as T;
}
